import {
  LocationHelpKind,
  LocationHelpOS,
  LocationHelpPlatform,
} from '@/views/composables/constants/main/locationHelp.constants';
import Bowser from 'bowser';

export interface LocationHelpClient {
  os: LocationHelpOS;
  // Bowser browser name, e.g. "Chrome", "Microsoft Edge".
  browser: string;
  browserMajor: number | null;
  isPWA: boolean;
}

export interface LocationHelpLine {
  key: string;
  values?: string[];
}

export interface LocationHelpContent {
  titleKey: string;
  introKey: string;
  steps: LocationHelpLine[];
  notes: LocationHelpLine[];
  ctaKey: string;
  reloads: boolean;
}

// Safari 26 moved site settings from "aA" into the page menu.
const IOS_SAFARI_PAGE_MENU_MAJOR = 26;

const BROWSER_LABELS: Record<string, string> = {
  Chrome: 'Chrome',
  Chromium: 'Chromium',
  Firefox: 'Firefox',
  'Microsoft Edge': 'Edge',
  Opera: 'Opera',
  Vivaldi: 'Vivaldi',
  'Samsung Internet for Android': 'Samsung Internet',
  Safari: 'Safari',
};

const IOS_APP_BROWSERS = new Set(['Chrome', 'Firefox', 'Microsoft Edge', 'Opera']);
const DESKTOP_CHROME_BROWSERS = new Set(['Chrome', 'Chromium', 'Vivaldi']);
// Steps that name the site in a list of sites.
const HOST_STEP_PLATFORMS = new Set([
  LocationHelpPlatform.DESKTOP_SAFARI,
  LocationHelpPlatform.ANDROID_SAMSUNG,
  LocationHelpPlatform.IOS_IN_APP,
]);

const ALLOW_STEPS: Record<LocationHelpPlatform, string[]> = {
  [LocationHelpPlatform.IOS_SAFARI]: [
    'location.help.ios_safari.1',
    'location.help.ios_safari.2',
    'location.help.ios_safari.3',
  ],
  [LocationHelpPlatform.IOS_SAFARI_LEGACY]: [
    'location.help.ios_safari_legacy.1',
    'location.help.ios_safari_legacy.2',
    'location.help.ios_safari_legacy.3',
  ],
  [LocationHelpPlatform.IOS_PWA]: ['location.help.ios_pwa.1', 'location.help.ios_pwa.2', 'location.help.ios_pwa.3'],
  [LocationHelpPlatform.IOS_APP_BROWSER]: ['location.help.ios_app_browser.1', 'location.help.ios_app_browser.2'],
  [LocationHelpPlatform.IOS_IN_APP]: ['location.help.ios_in_app.1'],
  [LocationHelpPlatform.ANDROID_CHROME]: ['location.help.android_chrome.1', 'location.help.android_chrome.2'],
  [LocationHelpPlatform.ANDROID_SAMSUNG]: ['location.help.android_samsung.1', 'location.help.android_samsung.2'],
  [LocationHelpPlatform.ANDROID_FIREFOX]: ['location.help.android_firefox.1', 'location.help.android_firefox.2'],
  [LocationHelpPlatform.ANDROID_PWA]: ['location.help.android_pwa.1', 'location.help.android_pwa.2'],
  [LocationHelpPlatform.DESKTOP_CHROME]: [
    'location.help.desktop_chrome.1',
    'location.help.desktop_chrome.2',
    'location.help.desktop_chrome.3',
  ],
  [LocationHelpPlatform.DESKTOP_EDGE]: [
    'location.help.desktop_edge.1',
    'location.help.desktop_edge.2',
    'location.help.desktop_edge.3',
  ],
  [LocationHelpPlatform.DESKTOP_FIREFOX]: [
    'location.help.desktop_firefox.1',
    'location.help.desktop_firefox.2',
    'location.help.desktop_firefox.3',
  ],
  [LocationHelpPlatform.DESKTOP_SAFARI]: [
    'location.help.desktop_safari.1',
    'location.help.desktop_safari.2',
    'location.help.desktop_safari.3',
  ],
  [LocationHelpPlatform.UNKNOWN]: ['location.help.unknown.1', 'location.help.unknown.2'],
};

const DEVICE_NOTES: Partial<Record<LocationHelpOS, string>> = {
  [LocationHelpOS.IOS]: 'location.help.note.device.ios',
  [LocationHelpOS.ANDROID]: 'location.help.note.device.android',
  [LocationHelpOS.MACOS]: 'location.help.note.device.macos',
  [LocationHelpOS.WINDOWS]: 'location.help.note.device.windows',
};

export function detectLocationHelpClient(
  userAgent: string,
  maxTouchPoints: number,
  isPWA: boolean,
): LocationHelpClient {
  const { browser, os } = Bowser.parse(userAgent);
  const major = parseInt(browser.version ?? '', 10);

  return {
    os: toLocationHelpOS(os.name, maxTouchPoints),
    browser: browser.name ?? '',
    browserMajor: Number.isNaN(major) ? null : major,
    isPWA,
  };
}

function toLocationHelpOS(osName: string | undefined, maxTouchPoints: number): LocationHelpOS {
  switch (osName) {
    case 'iOS':
      return LocationHelpOS.IOS;
    case 'Android':
      return LocationHelpOS.ANDROID;
    case 'macOS':
      // iPadOS asks for the desktop site and reports itself as macOS.
      return maxTouchPoints > 1 ? LocationHelpOS.IOS : LocationHelpOS.MACOS;
    case 'Windows':
      return LocationHelpOS.WINDOWS;
    default:
      return LocationHelpOS.OTHER;
  }
}

export function resolveLocationHelpPlatform(client: LocationHelpClient): LocationHelpPlatform {
  const { os, browser, browserMajor, isPWA } = client;

  if (os === LocationHelpOS.IOS) {
    if (isPWA) return LocationHelpPlatform.IOS_PWA;
    if (browser === 'Safari') {
      // Real Safari always sends Version/x; without it this is an app's built-in browser.
      if (browserMajor === null) return LocationHelpPlatform.IOS_IN_APP;
      return browserMajor >= IOS_SAFARI_PAGE_MENU_MAJOR
        ? LocationHelpPlatform.IOS_SAFARI
        : LocationHelpPlatform.IOS_SAFARI_LEGACY;
    }
    if (IOS_APP_BROWSERS.has(browser)) return LocationHelpPlatform.IOS_APP_BROWSER;
    return LocationHelpPlatform.IOS_IN_APP;
  }

  if (os === LocationHelpOS.ANDROID) {
    if (isPWA) return LocationHelpPlatform.ANDROID_PWA;
    if (browser === 'Samsung Internet for Android') return LocationHelpPlatform.ANDROID_SAMSUNG;
    if (browser === 'Firefox') return LocationHelpPlatform.ANDROID_FIREFOX;
    if (browser === 'Chrome' || browser === 'Chromium') return LocationHelpPlatform.ANDROID_CHROME;
    return LocationHelpPlatform.UNKNOWN;
  }

  if (browser === 'Safari' && os === LocationHelpOS.MACOS) return LocationHelpPlatform.DESKTOP_SAFARI;
  if (browser === 'Microsoft Edge') return LocationHelpPlatform.DESKTOP_EDGE;
  if (browser === 'Firefox') return LocationHelpPlatform.DESKTOP_FIREFOX;
  if (DESKTOP_CHROME_BROWSERS.has(browser)) return LocationHelpPlatform.DESKTOP_CHROME;
  return LocationHelpPlatform.UNKNOWN;
}

function getStepValues(platform: LocationHelpPlatform, appLabel: string, host: string): string[] | undefined {
  if (platform === LocationHelpPlatform.IOS_APP_BROWSER) return [appLabel];
  if (HOST_STEP_PLATFORMS.has(platform)) return [host];
  return undefined;
}

function getPreciseLine(
  client: LocationHelpClient,
  platform: LocationHelpPlatform,
  appLabel: string | null,
): LocationHelpLine | null {
  if (client.os === LocationHelpOS.IOS) {
    if (platform === LocationHelpPlatform.IOS_IN_APP) return null;
    if (platform === LocationHelpPlatform.IOS_APP_BROWSER && appLabel) {
      return { key: 'location.help.precise.ios_app', values: [appLabel] };
    }
    return { key: 'location.help.precise.ios_safari' };
  }

  if (client.os === LocationHelpOS.ANDROID) {
    if (!appLabel) return { key: 'location.help.precise.android_generic' };
    return { key: 'location.help.precise.android', values: [appLabel] };
  }

  // Desktop browsers have no approximate option.
  return null;
}

// Chrome and the other app browsers on iPhone have no per-site setting, and a
// site they once blocked stays blocked until the app is reinstalled.
function getFallbackLine(
  platform: LocationHelpPlatform,
  appLabel: string | null,
  host: string,
): LocationHelpLine | null {
  if (platform !== LocationHelpPlatform.IOS_APP_BROWSER) return null;
  return { key: 'location.help.note.ios_app_fallback', values: [host, appLabel ?? ''] };
}

export function buildLocationHelpContent(
  kind: LocationHelpKind,
  client: LocationHelpClient,
  host: string,
): LocationHelpContent {
  const platform = resolveLocationHelpPlatform(client);
  const appLabel = BROWSER_LABELS[client.browser] ?? null;
  const isPhone = client.os === LocationHelpOS.IOS || client.os === LocationHelpOS.ANDROID;
  const precise = getPreciseLine(client, platform, appLabel);
  const fallback = getFallbackLine(platform, appLabel, host);
  const stepValues = getStepValues(platform, appLabel ?? '', host);
  const allowSteps = ALLOW_STEPS[platform].map((key) => ({ key, values: stepValues }));

  if (kind === LocationHelpKind.APPROXIMATE) {
    return {
      titleKey: 'location.help.title.approximate',
      introKey: 'location.help.intro.approximate',
      // Built-in app browsers follow the app's own location setting, so the
      // only dependable fix there is the one for a blocked location.
      steps: precise ? [precise] : allowSteps,
      notes: [],
      ctaKey: 'location.help.cta.got_it',
      reloads: false,
    };
  }

  // Reloading inside an app's built-in browser changes nothing; the fix is to leave it.
  const steps: LocationHelpLine[] =
    platform === LocationHelpPlatform.IOS_IN_APP
      ? allowSteps
      : [...allowSteps, { key: isPhone ? 'location.help.step.reload_tap' : 'location.help.step.reload_click' }];

  const notes: LocationHelpLine[] = [];
  const deviceNote = DEVICE_NOTES[client.os];
  if (kind === LocationHelpKind.UNAVAILABLE && deviceNote) notes.push({ key: deviceNote });
  if (precise) notes.push(precise);
  if (fallback) notes.push(fallback);

  return {
    titleKey: kind === LocationHelpKind.BLOCKED ? 'location.help.title.blocked' : 'location.help.title.unavailable',
    introKey: 'location.help.intro.allow',
    steps,
    notes,
    ctaKey: 'location.help.cta.reload',
    reloads: true,
  };
}
